import mapboxgl from 'mapbox-gl';
import { mapConfig, validateMapConfig, getMapInitConfig } from '../config/mapConfig.js';
import { minesToGeoJSON, createMarkerElement, calculateMarkerColor, isTouchDevice, getTouchHitAreaExpression } from '../utils/mapHelpers.js';
import { getCommodityColor as getUtilsCommodityColor } from '../utils/commodityUtils.js';

class MapService {
  constructor() {
    this.map = null;
    this.markers = new Map();
    this.popup = null;
    this.sources = new Set();
    this.layers = new Set();
    this.eventHandlers = new Map();
    this.isInitialized = false;
    this.selectedMineId = null;
  }

  async initialize(container, options = {}) {
    try {
      validateMapConfig();

      mapboxgl.accessToken = mapConfig.mapbox.accessToken;

      const initConfig = getMapInitConfig();

      this.map = new mapboxgl.Map({
        ...initConfig,
        container,
        ...options
      });

      await this.setupMapEventHandlers();
      await this.addMapControls();

      return new Promise((resolve) => {
        this.map.on('load', () => {
          this.setupFogAndAtmosphere();
          this.applyMoroccoWorldview();
          this.isInitialized = true;
          resolve(this.map);
        });
      });
    } catch (error) {
      console.error('Failed to initialize map:', error);
      throw error;
    }
  }

  setupFogAndAtmosphere() {
    if (!this.map) return;

    this.map.setFog({
      'color': 'rgb(186, 210, 235)',
      'high-color': 'rgb(36, 92, 223)',
      'horizon-blend': 0.02,
      'space-color': 'rgb(11, 11, 25)',
      'star-intensity': 0.6
    });
  }

  /**
   * Apply Morocco (MA) worldview to administrative boundary layers
   * This removes the Western Sahara label and disputed boundary line
   */
  applyMoroccoWorldview() {
    if (!this.map) return;

    const worldview = 'MA';

    // Layers that contain worldview data in Mapbox light-v11 style
    const adminLayers = [
      'admin-0-boundary',
      'admin-0-boundary-bg',
      'admin-0-boundary-disputed',
      'admin-1-boundary',
      'admin-1-boundary-bg',
      'country-label'
    ];

    adminLayers.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        try {
          const existingFilter = this.map.getFilter(layerId);
          const worldviewFilter = ['match', ['get', 'worldview'], ['all', worldview], true, false];

          // Combine with existing filter if present
          const newFilter = existingFilter
            ? ['all', existingFilter, worldviewFilter]
            : worldviewFilter;

          this.map.setFilter(layerId, newFilter);
        } catch (error) {
          console.warn(`Could not apply worldview filter to ${layerId}:`, error);
        }
      }
    });

    console.log('Applied Morocco (MA) worldview to administrative boundaries');
  }

  async setupMapEventHandlers() {
    if (!this.map) return;

    this.map.on('moveend', () => {
      this.emit('viewportChanged', this.getViewport());
    });

    this.map.on('zoomend', () => {
      this.emit('zoomChanged', this.getZoom());
    });

    this.map.on('click', (e) => {
      this.emit('mapClick', e);
    });
  }

  addMapControls() {
    if (!this.map) return;

    if (mapConfig.controls.navigation) {
      this.map.addControl(new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true
      }), 'top-right');
    }

    if (mapConfig.controls.scale) {
      this.map.addControl(new mapboxgl.ScaleControl(), 'bottom-left');
    }

    if (mapConfig.controls.fullscreen) {
      this.map.addControl(new mapboxgl.FullscreenControl(), 'top-right');
    }

    if (mapConfig.controls.geolocate) {
      this.map.addControl(new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true
      }), 'top-right');
    }
  }

  updateMarkers(mines, scoredMineIdsFilter = null) {
    if (!this.map || !this.isInitialized) {
      console.warn('Map not initialized, cannot update markers');
      return;
    }

    console.log('🗺️ updateMarkers called with', mines?.length || 0, 'mines');

    // DEBUG: Check for specific mines
    const pintoValley = mines?.find(m => m.mine_id === 178832);
    const miami = mines?.find(m => m.mine_id === 162304);
    console.log('🔍 Pinto Valley (178832) in input:', pintoValley ? 'YES' : 'NO');
    console.log('🔍 Miami (162304) in input:', miami ? 'YES' : 'NO');

    // Apply scored mines filter if provided
    let filteredMines = mines;
    if (scoredMineIdsFilter && Array.isArray(scoredMineIdsFilter) && scoredMineIdsFilter.length > 0) {
      console.log('🔍 Applying scored mines filter with', scoredMineIdsFilter.length, 'IDs');
      console.log('🔍 Filter includes Pinto Valley:', scoredMineIdsFilter.includes(178832));
      console.log('🔍 Filter includes Miami:', scoredMineIdsFilter.includes(162304));

      filteredMines = mines.filter(mine => scoredMineIdsFilter.includes(mine.mine_id));
      console.log(`🔍 Filtered to ${filteredMines.length} scored mines (from ${mines.length} total)`);
    } else {
      console.log('✅ No scored mines filter applied');
    }

    const geojson = minesToGeoJSON(filteredMines);
    const isTouch = isTouchDevice();

    // Preserve selection indicator across layer rebuild
    const savedSelectionId = this.selectedMineId;
    const savedSelectionCoords = this.selectedMineCoordinates;

    this.removeExistingLayers();

    if (!this.map.getSource('mines')) {
      this.map.addSource('mines', {
        type: 'geojson',
        data: geojson,
        cluster: true,
        clusterRadius: 5,
        clusterMaxZoom: 22
      });
      this.sources.add('mines');
    } else {
      const source = this.map.getSource('mines');
      source.setData(geojson);
    }

    // Cluster bubble + count layers only render at zoom >= 7 (sub-national
    // view). At lower zooms the gray bubbles dominate the globe view, so we
    // hide them and let the regular mines-layer render clustered groups as
    // small dots instead.
    const CLUSTER_MIN_ZOOM = 7;

    this.map.addLayer({
      id: 'mine-clusters',
      type: 'circle',
      source: 'mines',
      minzoom: CLUSTER_MIN_ZOOM,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#374151',
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          7, 10,
          12, 13,
          18, 16
        ],
        'circle-stroke-width': 1.5,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.95
      }
    });
    this.layers.add('mine-clusters');

    this.map.addLayer({
      id: 'mine-cluster-count',
      type: 'symbol',
      source: 'mines',
      minzoom: CLUSTER_MIN_ZOOM,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['concat', '×', ['get', 'point_count_abbreviated']],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-ignore-placement': true
      },
      paint: {
        'text-color': '#ffffff'
      }
    });
    this.layers.add('mine-cluster-count');

    // Mapbox layer filters cannot reference the `zoom` expression, so the
    // "show clusters as gray dots at low zoom, show only individuals at high
    // zoom" split is done with two layers gated by maxzoom / minzoom instead
    // of a single zoom-aware filter.

    // Add invisible hit-area layer for touch devices
    // This provides larger tap targets while keeping visual markers small
    if (isTouch) {
      // Low-zoom hit-area: covers everything (including clusters)
      this.map.addLayer({
        id: 'mines-hit-area-low',
        type: 'circle',
        source: 'mines',
        maxzoom: CLUSTER_MIN_ZOOM,
        paint: {
          'circle-radius': getTouchHitAreaExpression(),
          'circle-color': 'transparent',
          'circle-opacity': 0
        }
      });
      this.layers.add('mines-hit-area-low');

      this.map.addLayer({
        id: 'mines-hit-area',
        type: 'circle',
        source: 'mines',
        minzoom: CLUSTER_MIN_ZOOM,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': getTouchHitAreaExpression(),
          'circle-color': 'transparent',
          'circle-opacity': 0
        }
      });
      this.layers.add('mines-hit-area');
    }

    // Common paint properties for the visual marker layers
    const minePaint = {
      'circle-color': ['case', ['has', 'point_count'], '#6B7280', ['get', 'color']],
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        1.5, ['case', ['==', ['get', 'is_artisanal'], true], 1,    1.5],
        3,   ['case', ['==', ['get', 'is_artisanal'], true], 1,    1.75],
        6,   ['case', ['==', ['get', 'is_artisanal'], true], 2,    4],
        8,   ['case', ['==', ['get', 'is_artisanal'], true], 4,    8],
        10,  ['case', ['==', ['get', 'is_artisanal'], true], 8,    15],
        12,  ['case', ['==', ['get', 'is_artisanal'], true], 13,   25],
        15,  ['case', ['==', ['get', 'is_artisanal'], true], 18,   35],
        18,  ['case', ['==', ['get', 'is_artisanal'], true], 23,   45]
      ],
      'circle-stroke-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        1, 0,
        6, 0.5,
        10, 1,
        15, 2
      ],
      'circle-stroke-color': '#ffffff',
      'circle-stroke-opacity': 0.8,
      'circle-opacity': ['case', ['==', ['get', 'is_artisanal'], true], 0.7, 0.9]
    };

    // Low-zoom layer: shows every feature, including clusters as gray dots
    this.map.addLayer({
      id: 'mines-low-zoom',
      type: 'circle',
      source: 'mines',
      maxzoom: CLUSTER_MIN_ZOOM,
      paint: minePaint
    });
    this.layers.add('mines-low-zoom');

    // High-zoom layer: individual mines only; cluster bubble layer above
    // takes over for grouped features.
    this.map.addLayer({
      id: 'mines-layer',
      type: 'circle',
      source: 'mines',
      minzoom: CLUSTER_MIN_ZOOM,
      filter: ['!', ['has', 'point_count']],
      paint: minePaint
    });
    this.layers.add('mines-layer');

    // Add selection indicator layer
    this.addSelectionIndicatorLayer();

    // Restore selection indicator if it was active before rebuild
    if (savedSelectionId != null && savedSelectionCoords) {
      this.updateSelectionIndicator(savedSelectionId, savedSelectionCoords);
    }

    // Click handler shared by every clickable mine layer. Cluster features
    // (no mine metadata) either zoom in or, if the underlying mines share
    // coordinates and zooming wouldn't help, fall back to a disambiguation
    // popup so the user can still pick one.
    const mineClickHandler = (e) => {
      if (!e.features || e.features.length === 0) return;
      const clusterFeature = e.features.find((f) => f.properties && f.properties.cluster);
      if (clusterFeature) {
        this._expandClusterOnClick(clusterFeature, e.lngLat);
        return;
      }
      if (isTouch && e.features.length > 1) {
        this.showDisambiguationPopup(e.features, e.lngLat);
      } else {
        this.showPopup(e.features[0]);
      }
    };

    const clickLayers = isTouch
      ? ['mines-hit-area-low', 'mines-hit-area']
      : ['mines-low-zoom', 'mines-layer'];
    clickLayers.forEach((layerId) => {
      if (this.map.getLayer(layerId)) {
        this.map.on('click', layerId, mineClickHandler);
      }
    });

    // Cluster bubble click at zoom >= CLUSTER_MIN_ZOOM
    this.map.on('click', 'mine-clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, { layers: ['mine-clusters'] });
      if (!features.length) return;
      this._expandClusterOnClick(features[0], e.lngLat);
    });

    // Desktop-only hover effects
    if (!isTouch) {
      const pointerOn = () => { this.map.getCanvas().style.cursor = 'pointer'; };
      const pointerOff = () => { this.map.getCanvas().style.cursor = ''; };
      ['mines-layer', 'mines-low-zoom', 'mine-clusters'].forEach((layerId) => {
        if (this.map.getLayer(layerId)) {
          this.map.on('mouseenter', layerId, pointerOn);
          this.map.on('mouseleave', layerId, pointerOff);
        }
      });
    }

    this.setupRenderedMinesNotifier();
  }

  _expandClusterOnClick(feature, clickLngLat) {
    if (!feature || !feature.properties) return;
    const clusterId = feature.properties.cluster_id;
    const coords = feature.geometry?.coordinates;
    const source = this.map?.getSource('mines');
    if (clusterId == null || !coords || !source) return;

    const pointCount = feature.properties.point_count || 0;
    const lngLat = clickLngLat || (Array.isArray(coords) ? { lng: coords[0], lat: coords[1] } : null);

    // Small clusters: check if the underlying mines share coordinates. If
    // they do, no amount of zooming will separate them (e.g., the three
    // Escondida mines all sit at -24.2717, -69.0700). Skip the zoom and
    // show a disambiguation popup so the user can still pick one.
    if (pointCount > 0 && pointCount <= 10) {
      source.getClusterLeaves(clusterId, pointCount, 0, (leafErr, leaves) => {
        if (leafErr || !leaves || leaves.length === 0) {
          this._zoomIntoCluster(clusterId, coords);
          return;
        }
        const first = leaves[0].geometry?.coordinates;
        const allSamePoint = first && leaves.every((leaf) => {
          const c = leaf.geometry?.coordinates;
          return c && Math.abs(c[0] - first[0]) < 1e-5 && Math.abs(c[1] - first[1]) < 1e-5;
        });
        if (allSamePoint && lngLat) {
          this.showDisambiguationPopup(leaves, lngLat);
        } else {
          this._zoomIntoCluster(clusterId, coords);
        }
      });
      return;
    }

    this._zoomIntoCluster(clusterId, coords);
  }

  _zoomIntoCluster(clusterId, coords) {
    const source = this.map?.getSource('mines');
    if (!source) return;
    source.getClusterExpansionZoom(clusterId, (err, expansionZoom) => {
      if (err) return;
      const targetZoom = Math.max(expansionZoom, this.map.getZoom() + 2);
      this.map.easeTo({ center: coords, zoom: targetZoom });
    });
  }

  setupRenderedMinesNotifier() {
    if (this.renderedMinesNotifierWired) return;
    this.renderedMinesNotifierWired = true;
    const trigger = () => this.notifyRenderedMines();
    this.map.on('idle', trigger);
    this.map.on('moveend', trigger);
  }

  notifyRenderedMines() {
    if (this.renderedMinesDebounce) return;
    this.renderedMinesDebounce = setTimeout(() => {
      this.renderedMinesDebounce = null;
      this._dispatchRenderedMines().catch((err) =>
        console.warn('Failed to compute rendered mines for legend:', err)
      );
    }, 150);
  }

  async _dispatchRenderedMines() {
    if (!this.map || !this.map.getSource('mines')) return;
    const layers = [];
    if (this.map.getLayer('mines-layer')) layers.push('mines-layer');
    if (this.map.getLayer('mines-low-zoom')) layers.push('mines-low-zoom');
    if (this.map.getLayer('mine-clusters')) layers.push('mine-clusters');
    if (!layers.length) return;

    const features = this.map.queryRenderedFeatures(undefined, { layers });
    const source = this.map.getSource('mines');
    const seen = new Set();
    const mines = [];

    const clusterExpansions = [];
    for (const feat of features) {
      const props = feat.properties || {};
      if (props.cluster) {
        clusterExpansions.push(this._expandCluster(source, props.cluster_id, props.point_count));
      } else if (props.mine_id != null && !seen.has(props.mine_id)) {
        seen.add(props.mine_id);
        mines.push(props);
      }
    }

    const leafGroups = await Promise.all(clusterExpansions);
    for (const leaves of leafGroups) {
      for (const leaf of leaves) {
        const id = leaf?.mine_id;
        if (id != null && !seen.has(id)) {
          seen.add(id);
          mines.push(leaf);
        }
      }
    }

    document.dispatchEvent(new CustomEvent('mines-rendered', { detail: { mines } }));
  }

  _expandCluster(source, clusterId, pointCount) {
    return new Promise((resolve) => {
      source.getClusterLeaves(clusterId, pointCount || Infinity, 0, (err, leaves) => {
        if (err || !leaves) return resolve([]);
        resolve(leaves.map((leaf) => leaf.properties || {}));
      });
    });
  }

  addSelectionIndicatorLayer() {
    // Add source for selected mine indicator
    if (!this.map.getSource('selected-mine')) {
      this.map.addSource('selected-mine', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
      this.sources.add('selected-mine');
    }

    // Add outer ring layer for selection indicator
    this.map.addLayer({
      id: 'selected-mine-ring',
      type: 'circle',
      source: 'selected-mine',
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          1.5, 8,    // Larger than regular mines
          3, 10,
          6, 18,
          8, 28,
          10, 40,
          12, 55,
          15, 75,
          18, 95
        ],
        'circle-color': 'transparent',
        'circle-stroke-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          1, 2,
          6, 3,
          10, 4,
          15, 5
        ],
        'circle-stroke-color': '#FFD700', // Gold ring
        'circle-stroke-opacity': 0.9
      }
    });
    this.layers.add('selected-mine-ring');

    // Add inner pulse ring for extra emphasis
    this.map.addLayer({
      id: 'selected-mine-pulse',
      type: 'circle',
      source: 'selected-mine',
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          1.5, 12,   // Even larger for pulse effect
          3, 14,
          6, 22,
          8, 32,
          10, 44,
          12, 59,
          15, 79,
          18, 99
        ],
        'circle-color': 'transparent',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FF6B35', // Orange pulse ring
        'circle-stroke-opacity': 0.6
      }
    });
    this.layers.add('selected-mine-pulse');
  }

  showPopup(feature) {
    if (!this.map || !feature) return;

    const { coordinates } = feature.geometry;
    const { properties } = feature;

    console.log('Popup feature properties:', properties);

    const popupContent = this.createPopupContent(properties);

    if (this.popup) {
      this.popup.remove();
    }

    this.popup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      className: 'mine-popup'
    })
      .setLngLat(coordinates)
      .setHTML(popupContent)
      .addTo(this.map);
  }

  showDisambiguationPopup(features, lngLat) {
    if (!this.map || !features || features.length === 0) return;

    // Sort by distance from tap point (closest first)
    const sortedFeatures = features.slice(0, 5); // Limit to 5 closest mines

    const popupContent = this.createDisambiguationContent(sortedFeatures);

    if (this.popup) {
      this.popup.remove();
    }

    this.popup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      className: 'mine-popup disambiguation-popup'
    })
      .setLngLat(lngLat)
      .setHTML(popupContent)
      .addTo(this.map);

    // Add click handlers for mine selection
    setTimeout(() => {
      sortedFeatures.forEach((feature, index) => {
        const button = document.getElementById(`select-mine-${index}`);
        if (button) {
          button.addEventListener('click', () => {
            this.showPopup(feature);
          });
        }
      });
    }, 0);
  }

  createDisambiguationContent(features) {
    const minesList = features.map((feature, index) => {
      const props = feature.properties;
      const mineName = props.mine_name || 'Unnamed Mine';
      const commodity = props.commodity || 'Unknown';
      const countryName = props.country_name || 'Unknown';
      const commodityColor = this.getCommodityColor(props.legend_group, props.commodity_category, props.commodity);

      return `
        <button id="select-mine-${index}" class="disambiguation-item">
          <div class="disambiguation-marker" style="background-color: ${commodityColor}"></div>
          <div class="disambiguation-details">
            <div class="disambiguation-name">${mineName}</div>
            <div class="disambiguation-meta">${commodity} • ${countryName}</div>
          </div>
        </button>
      `;
    }).join('');

    return `
      <div class="disambiguation-popup-content">
        <div class="disambiguation-header">
          <h4>Select Mine</h4>
          <p>Multiple mines detected. Tap one to view details:</p>
        </div>
        <div class="disambiguation-list">
          ${minesList}
        </div>
      </div>
    `;
  }

  createPopupContent(properties) {
    const {
      mine_id,
      mine_name,
      company,
      commodity,
      country_name,
      mine_status,
      risk_level,
      overall_risk_score,
      mine_type,
      commodity_category,
      latitude,
      longitude,
      legend_group,
      mine_risk_aggregate, // NEW: Contains our detailed risk scoring data
      is_artisanal,
      data_source,
      secondary_commodities_csv
    } = properties;
    // secondary_commodities_csv lists byproducts so a mine surfaced via a
    // junction-only filter match (e.g. silver in a copper mine) is self-explanatory.
    const secondaryCommodities = (secondary_commodities_csv || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    // Risk level styling - prioritize our new risk scoring structure
    let riskLevel;
    let riskScore = null;
    let riskClassification = null;
    let completeness = null;

    // Check for flattened risk properties (new structure)
    const {
      risk_overall_score,
      risk_classification,
      risk_completeness_percentage
    } = properties;

    // Debug logging
    console.log('Popup properties for:', mine_name, {
      has_risk_classification: !!risk_classification,
      risk_classification: risk_classification,
      risk_overall_score: risk_overall_score,
      overall_risk_score: overall_risk_score
    });

    if (risk_classification) {
      // Use new risk scoring data (flattened properties)
      riskScore = parseFloat(risk_overall_score);
      riskClassification = risk_classification; // MINIMAL/LOW/MEDIUM/HIGH/EXTREME
      completeness = parseFloat(risk_completeness_percentage);
      riskLevel = riskClassification.toLowerCase();
    } else if (overall_risk_score !== undefined && overall_risk_score !== null) {
      // Fallback to legacy risk score
      const score = parseFloat(overall_risk_score);
      riskLevel = this.getRiskCategoryFromScore(score);
      riskScore = score;
    } else {
      // Fallback to country-based risk level
      riskLevel = risk_level || 'medium';
    }

    // Check for SUSPENDED override
    const isSuspendedOverride = mine_status === 'Suspended' &&
                                 riskClassification === 'EXTREME' &&
                                 riskScore < 8.0;

    const riskStyles = this.getRiskStyles(riskLevel);

    // Commodity color based on legend group and commodity name for consistency with legend
    const commodityColor = this.getCommodityColor(legend_group, commodity_category, commodity);

    // Format coordinates
    const coords = latitude && longitude
      ? `${parseFloat(longitude).toFixed(4)}, ${parseFloat(latitude).toFixed(4)}`
      : 'Unknown';

    // Calculate risk bar width percentage
    let riskBarWidth = 60; // Default medium risk
    if (riskScore !== undefined && riskScore !== null) {
      const rawScore = parseFloat(riskScore);
      // Convert 0-10 scale to 0-100 percentage for display
      riskBarWidth = rawScore <= 10 ? rawScore * 10 : rawScore;
    }

    return `
      <div class="mine-popup">
        <div class="popup-header">
          <h3>${mine_name || 'Unnamed Mine'}</h3>
          <div class="popup-tags">
            <div class="popup-tag">
              <div class="tag-dot" style="background-color: ${commodityColor}"></div>
              ${commodity || 'Unknown'}
            </div>
            ${mine_type ? `<div class="popup-tag">${mine_type}</div>` : ''}
            ${mine_status ? `<div class="popup-tag">${mine_status}</div>` : ''}
          </div>
          ${secondaryCommodities.length > 0 ? `
          <div class="popup-secondary-commodities">
            <span class="popup-secondary-label">Also produces:</span>
            ${secondaryCommodities.map((c) => `<span class="popup-secondary-pill">${c}</span>`).join('')}
          </div>
          ` : ''}
        </div>
        <div class="popup-content">
          ${isSuspendedOverride ? `
          <div class="popup-suspended-notice" style="background-color: #ff4444; color: white; padding: 8px; margin-bottom: 8px; border-radius: 4px; font-size: 11px;">
            <strong>⚠️ SUSPENDED:</strong> EXTREME risk due to operational halt
          </div>
          ` : ''}
          <div class="popup-section">
            <div class="popup-section-title">Risk Assessment</div>
            <div class="risk-source-note">${risk_classification ? 'Site-level risk assessment' : 'Default country-level risk score'}</div>
            <div class="risk-indicator">
              <div class="risk-bar">
                <div class="risk-level ${riskStyles.class}" style="width: ${Math.min(100, Math.max(0, riskBarWidth))}%"></div>
              </div>
              <div class="risk-value ${riskStyles.textClass}">
                ${riskClassification || riskStyles.label}
              </div>
            </div>
          </div>
          <div class="popup-section">
            <div class="popup-section-title">Details</div>
            <div class="popup-row">
              <div class="popup-label">Country</div>
              <div class="popup-value">${country_name || 'Unknown'}</div>
            </div>
            ${company ? `
            <div class="popup-row">
              <div class="popup-label">Company</div>
              <div class="popup-value">${company}</div>
            </div>` : ''}
            <div class="popup-row">
              <div class="popup-label">Coordinates</div>
              <div class="popup-value">${coords}</div>
            </div>
          </div>
          ${is_artisanal && data_source === 'Stimson' ? `<div class="popup-source">Source: <a href="https://www.stimson.org/program/mekong/" target="_blank" rel="noopener">Stimson Center</a> / Planet Labs</div>` : is_artisanal ? `<div class="popup-source">Source: <a href="https://ipisresearch.be/home/maps-data/open-data/" target="_blank" rel="noopener">IPIS Open Data</a> (ODC-By)</div>` : ''}
          <div class="popup-buttons">
            <button class="popup-button" onclick="window.app?.components?.mineDetailsPanel?.showMine(${mine_id || 'null'})">
              View Full Details
            </button>
            <button class="popup-button risk-details-btn" onclick="window.app?.components?.riskDetailsPanel?.show({mine_id: ${mine_id || 'null'}, mine_name: '${(mine_name || '').replace(/'/g, "\\'")}', country_name: '${(country_name || '').replace(/'/g, "\\'")}', primary_commodity: '${(commodity || '').replace(/'/g, "\\'")}', status: '${(mine_status || '').replace(/'/g, "\\'")}', mine_type: '${(mine_type || '').replace(/'/g, "\\'")}' })">
              Risk Details
            </button>
          </div>
        </div>
      </div>
    `;
  }

  getRiskCategoryFromScore(score) {
    // Convert 0-10 risk score to category
    // Thresholds: 0-2=minimal, 2.1-4=low, 4.1-6=medium, 6.1-8=high, 8.1-10=extreme
    if (score > 8) return 'extreme';
    if (score > 6) return 'high';
    if (score > 4) return 'medium';
    if (score > 2) return 'low';
    return 'minimal';
  }

  getRiskStyles(riskLevel) {
    const styles = {
      // New risk scoring classifications (MINIMAL/LOW/MEDIUM/HIGH/EXTREME)
      'minimal': { class: 'risk-minimal', textClass: 'risk-minimal-text', label: 'Minimal' },
      'low': { class: 'risk-low', textClass: 'risk-low-text', label: 'Low' },
      'medium': { class: 'risk-medium', textClass: 'risk-medium-text', label: 'Medium' },
      'high': { class: 'risk-high', textClass: 'risk-high-text', label: 'High' },
      'extreme': { class: 'risk-extreme', textClass: 'risk-extreme-text', label: 'Extreme' },
      // Legacy mappings for backwards compatibility
      'very-low': { class: 'risk-minimal', textClass: 'risk-minimal-text', label: 'Very Low' },
      'very-high': { class: 'risk-extreme', textClass: 'risk-extreme-text', label: 'Very High' }
    };

    const normalizedLevel = riskLevel.toLowerCase().replace(/\s+/g, '-');
    return styles[normalizedLevel] || styles.medium;
  }

  getCommodityColor(legendGroup, commodityCategory, commodity) {
    // Use the same color function as the legend for consistency
    if (commodity) {
      try {
        return getUtilsCommodityColor(commodity);
      } catch (error) {
        console.warn('Error getting standard commodity color, using fallback:', error);
      }
    }

    // Fallback color mapping based on legend group and category
    const colors = {
      'high-value': '#FF6B6B',
      'critical': '#FF6B6B',      // Use same color as critical minerals
      'non-critical': '#95A5A6'
    };

    if (legendGroup === 'high-value') {
      return colors['high-value'];
    } else if (commodityCategory === 'critical' || legendGroup === 'critical') {
      return colors['critical'];
    } else {
      return colors['non-critical'];
    }
  }

  // Fallback method for commodity color calculation (in case import fails)
  getStandardCommodityColor(commodity) {
    // This is a simplified version of the commodityUtils function
    if (!commodity || typeof commodity !== 'string') {
      return '#6B7280';
    }

    const normalizedCommodity = commodity.toLowerCase().trim();

    // High-value specific colors (simplified)
    const specificColors = {
      'copper': '#B87333',
      'lithium': '#9B59B6',
      'cobalt': '#2980B9',
      'nickel': '#95A5A6',
      'rare earth elements': '#E74C3C',
      'platinum': '#E5007D',
      'gold': '#FFD700'
    };

    if (specificColors[normalizedCommodity]) {
      return specificColors[normalizedCommodity];
    }

    // Critical minerals
    const criticalMinerals = [
      'aluminum', 'chromium', 'cobalt', 'copper', 'graphite', 'iridium',
      'iron ore', 'lithium', 'manganese', 'nickel', 'platinum', 'rare earth',
      'silicon', 'tin', 'titanium', 'tungsten', 'vanadium', 'zinc'
    ];

    const isCritical = criticalMinerals.some(critical =>
      normalizedCommodity.includes(critical.toLowerCase())
    );

    if (isCritical) {
      return '#FF6B6B';
    }

    // Default for non-critical
    return '#95A5A6';
  }

  removeExistingLayers() {
    if (!this.map) return;

    this.layers.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.off('click', layerId);
        this.map.off('mouseenter', layerId);
        this.map.off('mouseleave', layerId);
        this.map.removeLayer(layerId);
      }
    });
    this.layers.clear();

    this.sources.forEach(sourceId => {
      if (this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId);
      }
    });
    this.sources.clear();
  }

  clearMarkers() {
    this.markers.forEach(marker => marker.remove());
    this.markers.clear();
  }

  getBounds() {
    if (!this.map) return null;

    const bounds = this.map.getBounds();

    // Normalize longitude values to valid range (-180 to 180)
    const normalizeLng = (lng) => {
      while (lng > 180) lng -= 360;
      while (lng < -180) lng += 360;
      return lng;
    };

    // Clamp latitude values to valid range (-90 to 90)
    const clampLat = (lat) => Math.max(-90, Math.min(90, lat));

    return {
      north: clampLat(bounds.getNorth()),
      south: clampLat(bounds.getSouth()),
      east: normalizeLng(bounds.getEast()),
      west: normalizeLng(bounds.getWest())
    };
  }

  getZoom() {
    return this.map ? this.map.getZoom() : null;
  }

  getCenter() {
    if (!this.map) return null;

    const center = this.map.getCenter();
    return [center.lng, center.lat];
  }

  getViewport() {
    return {
      bounds: this.getBounds(),
      zoom: this.getZoom(),
      center: this.getCenter()
    };
  }

  setViewport(viewport) {
    if (!this.map) return;

    if (viewport.bounds) {
      this.map.fitBounds([
        [viewport.bounds.west, viewport.bounds.south],
        [viewport.bounds.east, viewport.bounds.north]
      ]);
    } else if (viewport.center && viewport.zoom !== undefined) {
      this.map.setCenter(viewport.center);
      this.map.setZoom(viewport.zoom);
    }
  }

  flyTo(center, zoom = null) {
    if (!this.map) return;

    const options = { center };
    if (zoom !== null) {
      options.zoom = zoom;
    }

    this.map.flyTo(options);
  }

  fitBounds(bounds, options = {}) {
    if (!this.map) return;

    // bounds expected as [[west, south], [east, north]] or LngLatBoundsLike
    this.map.fitBounds(bounds, {
      padding: options.padding || 50,
      maxZoom: options.maxZoom || 15,
      ...options
    });
  }

  zoomIn() {
    if (!this.map) return;
    this.map.zoomIn();
  }

  zoomOut() {
    if (!this.map) return;
    this.map.zoomOut();
  }

  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event).add(handler);
  }

  off(event, handler) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).delete(handler);
    }
  }

  emit(event, data) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Update the selection indicator to highlight a specific mine
   * @param {number} mineId - Mine ID to highlight
   * @param {Array} coordinates - [longitude, latitude] coordinates of the mine
   */
  updateSelectionIndicator(mineId, coordinates) {
    if (!this.map || !this.isInitialized) return;

    // Clear previous selection
    this.clearSelectionIndicator();

    // Set new selection
    this.selectedMineId = mineId;
    this.selectedMineCoordinates = coordinates;

    if (coordinates && coordinates.length === 2) {
      const selectedMineSource = this.map.getSource('selected-mine');
      if (selectedMineSource) {
        selectedMineSource.setData({
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: coordinates
            },
            properties: {
              mine_id: mineId
            }
          }]
        });
        console.log('🎯 Selection indicator updated for mine:', mineId);
      }
    }
  }

  /**
   * Clear the selection indicator
   */
  clearSelectionIndicator() {
    if (!this.map || !this.isInitialized) return;

    this.selectedMineId = null;
    this.selectedMineCoordinates = null;
    const selectedMineSource = this.map.getSource('selected-mine');
    if (selectedMineSource) {
      selectedMineSource.setData({
        type: 'FeatureCollection',
        features: []
      });
    }
  }

  /**
   * Highlight and show details for a specific mine from search results
   * @param {Object} searchResult - Search result object with mine information
   */
  highlightSearchResult(searchResult) {
    if (!this.map || !this.isInitialized) {
      console.warn('Map not ready for highlighting');
      return;
    }

    // For mine type results, try to find and highlight the mine
    if (searchResult.type === 'mine' && searchResult.id) {
      // Just try to find and show popup - selection indicator handled separately
      this.highlightMineById(searchResult.id);
    }
  }

  /**
   * Find and highlight a mine by its ID
   * @param {string|number} mineId - Mine ID to highlight
   */
  highlightMineById(mineId) {
    try {
      // Query the map source for the specific mine
      const minesSource = this.map.getSource('mines');
      if (!minesSource) {
        console.warn('Mines source not found');
        return;
      }

      // Get all features from the source
      const features = this.map.querySourceFeatures('mines', {
        sourceLayer: null
      });

      // Find the matching mine feature
      const targetFeature = features.find(feature => {
        const props = feature.properties;
        return props.mine_id == mineId || props.id == mineId;
      });

      if (targetFeature) {
        console.log('Found mine for highlighting:', targetFeature.properties.mine_name);
        this.showPopup(targetFeature);

        // Emit mine clicked event for consistency with click behavior
        this.emit('mineClicked', targetFeature.properties);
        return true; // Mine was found and highlighted
      } else {
        console.warn('Mine not found on map:', mineId, '- mine may not be loaded in current viewport');
        // The mine might not be in the current viewport or might be filtered out
        // Could implement a fallback popup here if needed
        return false; // Mine was not found
      }

    } catch (error) {
      console.error('Error highlighting mine:', error);
    }
  }

  /**
   * Close any open popups
   */
  closePopup() {
    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }
  }

  resize() {
    if (this.map) {
      this.map.resize();
    }
  }

  destroy() {
    this.clearMarkers();
    this.clearSelectionIndicator();
    this.removeExistingLayers();

    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }

    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    this.eventHandlers.clear();
    this.isInitialized = false;
  }

  isReady() {
    return this.isInitialized && this.map && this.map.loaded();
  }
}

export const mapService = new MapService();